"use client";

import { useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { Archive, ChevronDown, Clock, Download, MapPin, Pencil, Plus, Search } from "lucide-react";
import {
  bySchedule,
  cn,
  formatTime,
  inScope,
  iso,
  ordinal,
  parseIso,
  scopeLabel,
  targetEmployees,
  teamIndex,
  withAlpha,
  ALL_SCOPE,
  STATUS_META,
  STATUS_ORDER,
  type Itinerary,
  type Scope,
  type Status,
} from "@routecore/core";
import { useStudio } from "@routecore/app-shell";
import {
  ConfirmDelete,
  EmployeeDot,
  ScopeFilter,
  GhostButton,
  PeriodNav,
  PrimaryButton,
  Segmented,
  StyledSelect,
} from "@routecore/controls";
import DatePicker from "@routecore/date-picker";
import { RollingNumber } from "@routecore/motion-fx";

type Period = "all" | "month" | "week" | "day";
type StatusFilter = "all" | Status;

export default function RecordsView() {
  const {
    employees,
    teams,
    itineraries,
    openItineraryModal,
    openEmployeeModal,
    openTeamModal,
    updateItinerary,
    removeItinerary,
    toast,
    currentUser,
    isPrivileged,
    canEditEmployee,
    canEditItinerary,
  } = useStudio();

  const [period, setPeriod] = useState<Period>("all");
  const [cursor, setCursor] = useState(() => new Date());
  const [scope, setScope] = useState<Scope>(ALL_SCOPE);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");
  const teamOf = useMemo(() => teamIndex(employees), [employees]);
  const anyWritable = useMemo(
    () => isPrivileged || employees.some((e) => canEditEmployee(e)),
    [employees, isPrivileged, canEditEmployee],
  );
  const isSuperAdmin = useMemo(
    () => !!currentUser?.is_super_admin,
    [currentUser],
  );

  const { start, end, label } = useMemo(() => {
    if (period === "month") {
      return {
        start: startOfMonth(cursor),
        end: endOfMonth(cursor),
        label: format(cursor, "MMMM yyyy"),
      };
    }
    if (period === "week") {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      const e = endOfWeek(cursor, { weekStartsOn: 1 });
      return { start: s, end: e, label: `${format(s, "MMM d")} – ${format(e, "MMM d")}` };
    }
    if (period === "day") {
      return {
        start: startOfDay(cursor),
        end: endOfDay(cursor),
        label: format(cursor, "EEE, MMM d"),
      };
    }
    return { start: null, end: null, label: "All time" };
  }, [period, cursor]);

  const filtered = useMemo(() => {
    const s = start ? iso(start) : null;
    const e = end ? iso(end) : null;
    const q = query.trim().toLowerCase();
    return itineraries
      .filter((i) => {
        if (s && e && (i.date < s || i.date > e)) return false;
        if (!inScope(i, scope, teamOf)) return false;
        if (statusFilter !== "all" && i.status !== statusFilter) return false;
        if (q) {
          const hay = `${i.title} ${i.notes} ${i.employeeName ?? ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (a.date === b.date ? bySchedule(a, b) : a.date < b.date ? 1 : -1));
  }, [itineraries, start, end, scope, teamOf, statusFilter, query]);

  /** Activity dots for the mini calendar — respects team/employee/status/search. */
  const dayMarkers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const m = new Map<string, number>();
    for (const it of itineraries) {
      if (!inScope(it, scope, teamOf)) continue;
      if (statusFilter !== "all" && it.status !== statusFilter) continue;
      if (q && !`${it.title} ${it.notes} ${it.employeeName ?? ""}`.toLowerCase().includes(q)) {
        continue;
      }
      m.set(it.date, (m.get(it.date) ?? 0) + 1);
    }
    return m;
  }, [itineraries, scope, teamOf, statusFilter, query]);

  const groups = useMemo(() => {
    const out: { date: string; items: Itinerary[] }[] = [];
    for (const it of filtered) {
      const last = out[out.length - 1];
      if (last && last.date === it.date) last.items.push(it);
      else out.push({ date: it.date, items: [it] });
    }
    return out;
  }, [filtered]);

  const step = (dir: 1 | -1) => {
    if (period === "month") setCursor((c) => addMonths(c, dir));
    else if (period === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addDays(c, dir));
  };

  /** Downloads `blob` via a DOM-attached anchor with a late revoke. */
  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Keep the object URL alive until the browser has picked the download up.
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast("Nothing to export for the current filters", "info");
      return;
    }

    const generated = new Date();
    const esc = (v: string | number | null | undefined) =>
      `"${String(v ?? "").replace(/"/g, '""')}"`;
    const row = (cells: (string | number | null | undefined)[]) => cells.map(esc).join(",");

    /* ---------- report header ---------- */
    const lines: string[] = [
      esc("iSource RouteCore — Team Itinerary System"),
      esc("Itinerary Records Report"),
      "",
      row(["Report generated", format(generated, "yyyy-MM-dd HH:mm")]),
      row(["Period", label]),
      row(["Scope", scopeLabel(scope, teams, employees)]),
      row([
        "Status filter",
        statusFilter === "all" ? "Any status" : STATUS_META[statusFilter].label,
      ]),
      row(["Search", query.trim() || "—"]),
      row(["Rows included", filtered.length]),
      "",
      row([
        "Date",
        "Time",
        "Employee",
        "Role",
        "Team",
        "Seq",
        "Title",
        "Location",
        "Coordinates",
        "Status",
        "Notes",
      ]),
    ];

    /* ---------- filtered data ---------- */
    for (const it of filtered) {
      const emp = employees.find((e) => e.id === it.employeeId);
      const team = teams.find((t) => t.id === emp?.teamId);
      lines.push(
        row([
          it.date,
          it.appointmentTime ? formatTime(it.appointmentTime) : "",
          it.employeeName ?? "",
          emp?.role ?? "",
          team?.name ?? "",
          it.seq,
          it.title,
          it.locationName,
          it.lat != null && it.lng != null ? `${it.lat.toFixed(6)}, ${it.lng.toFixed(6)}` : "",
          STATUS_META[it.status].label,
          it.notes,
        ]),
      );
    }

    /* ---------- summary ---------- */
    lines.push("", esc("Summary"));
    lines.push(row(["Total itineraries", filtered.length]));
    for (const s of STATUS_ORDER) {
      lines.push(row([STATUS_META[s].label, filtered.filter((i) => i.status === s).length]));
    }
    for (const e of targetEmployees(employees, scope)) {
      const team = teams.find((t) => t.id === e.teamId);
      lines.push(
        row([`${e.name} — ${team?.name ?? "Unassigned"}`, filtered.filter((i) => i.employeeId === e.id).length]),
      );
    }

    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const filename = `isource-routecore-records-${period}-${iso(generated)}.csv`;
    const n = filtered.length;

    // Embedded previews (iframes) block direct downloads — hand off to a
    // top-level tab where the browser permits the file save.
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }

    if (inIframe) {
      const w = window.open("", "_blank");
      if (w) {
        w.document.title = "iSource RouteCore — Export ready";
        w.document.body.innerHTML = `
          <div style="min-height:100vh;margin:0;display:grid;place-items:center;background:#0F1E3A;font-family:ui-sans-serif,system-ui,-apple-system,sans-serif;color:#FFFFFF">
            <div style="max-width:430px;margin:24px;padding:42px 36px;background:#172745;border:1px solid #2A3E66;border-radius:24px;box-shadow:0 24px 48px -20px rgba(0,0,0,.7);text-align:center">
              <div style="width:56px;height:56px;margin:0 auto 18px;border-radius:18px;background:#1A2C4E;border:1px solid #2A3E66;display:grid;place-items:center;padding:8px;box-shadow:0 2px 10px rgba(0,0,0,.4)">
                <img src="/iSource LOGO1.png" alt="iSource" style="width:100%;height:100%;object-fit:contain" />
              </div>
              <h1 style="margin:0;font-size:22px;letter-spacing:-0.02em;color:#FFFFFF">Your report is ready</h1>
              <p style="margin:10px 0 0;font-size:13px;color:#8EA3C7">iSource RouteCore · Itinerary Records</p>
              <p style="margin:2px 0 26px;font-size:13px;color:#8EA3C7"><strong style="color:#FFFFFF">${n}</strong> ${n === 1 ? "itinerary" : "itineraries"} · ${period === "all" ? "all time" : period}</p>
              <a id="wp-export-btn" href="${URL.createObjectURL(blob)}" download="${filename}" style="display:inline-flex;align-items:center;gap:9px;background:#FACC15;color:#0F1E3A;padding:14px 30px;border-radius:999px;font-weight:700;text-decoration:none;font-size:14px;box-shadow:0 4px 18px rgba(250,204,21,.28)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download CSV report
              </a>
              <p style="margin:24px 0 0;font-size:11px;color:#6B83A8;line-height:1.7">Embedded previews open exports in a new tab.<br>If nothing downloaded automatically, click the button above.</p>
            </div>
          </div>`;
        const btn = w.document.getElementById("wp-export-btn");
        if (btn) btn.click();
        toast("Export opened in a new tab", "info");
        return;
      }
    }

    triggerDownload(blob, filename);
    toast(`CSV report exported — ${n} ${n === 1 ? "itinerary" : "itineraries"}`);
  };

  return (
    <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-none">
      {/* header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Archive className="size-3.5 text-accent" />
            <span className="text-[10px] font-bold tracking-[0.24em] text-ink-faint uppercase">
              Records · The captain&apos;s log
            </span>
          </div>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            Every journey<span className="text-ink-faint">, on record.</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <GhostButton icon={Download} onClick={exportCsv}>
            Export CSV
          </GhostButton>
          <PrimaryButton
            icon={Plus}
            disabled={!anyWritable}
            onClick={() => openItineraryModal({})}
          >
            {anyWritable ? "New itinerary" : "🔒 No bound profile"}
          </PrimaryButton>
        </div>
      </header>

      {/* controls */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Segmented<Period>
          id="records-period"
          value={period}
          onChange={setPeriod}
          options={[
            { value: "all", label: "All time" },
            { value: "month", label: "Month" },
            { value: "week", label: "Week" },
            { value: "day", label: "Day" },
          ]}
        />
        {period === "day" && (
          <DatePicker value={cursor} onChange={setCursor} markers={dayMarkers} />
        )}
        {(period === "month" || period === "week") && (
          <PeriodNav
            label={label}
            onPrev={() => step(-1)}
            onNext={() => step(1)}
            onReset={() => setCursor(new Date())}
          />
        )}
        <StyledSelect value={statusFilter} onChange={(v) => setStatusFilter(v as StatusFilter)} className="w-[150px]">
          <option value="all">Any status</option>
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {STATUS_META[s].label}
            </option>
          ))}
        </StyledSelect>
        <div className="relative min-w-[190px] flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-ink-faint" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search titles, notes, people…"
            className="w-full rounded-full border border-line bg-porcelain py-2.5 pr-4 pl-10 text-sm placeholder:text-ink-faint focus:border-accent focus:outline-none"
          />
        </div>
      </div>
      <div className="mt-3">
        <ScopeFilter
          employees={employees}
          teams={teams}
          scope={scope}
          onChange={setScope}
          onAdd={isPrivileged ? () => openEmployeeModal() : undefined}
          onManageTeams={isPrivileged ? openTeamModal : undefined}
        />
      </div>

      {/* ledger */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-porcelain shadow-card">
        <div className="max-h-[62vh] overflow-auto">
          {groups.length === 0 ? (
            <div className="grid place-items-center gap-2 py-20 text-center">
              <Archive className="size-6 text-ink-faint" />
              <p className="font-display text-xl text-ink-soft italic">
                Nothing logged for this slice of time.
              </p>
              <button
                type="button"
                onClick={() => {
                  setPeriod("all");
                  setQuery("");
                  setStatusFilter("all");
                  setScope(ALL_SCOPE);
                }}
                className="mt-1 text-xs font-bold text-accent underline-offset-4 hover:underline"
              >
                Clear every filter
              </button>
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.date}>
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-cream/95 px-4 py-2.5 backdrop-blur-sm">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-ink-soft uppercase">
                    {format(parseIso(g.date), "EEEE, MMMM d, yyyy")}
                  </span>
                  <span className="rounded-full bg-ink/5 px-2 py-0.5 text-[10px] font-bold text-ink-soft tabular-nums">
                    {g.items.length}
                  </span>
                </div>
                {g.items.map((it) => {
                  const itWritable = canEditItinerary(it);
                  return (
                    <RecordRow
                      key={it.id}
                      it={it}
                      itWritable={itWritable}
                      statusEditable={isSuperAdmin}
                      onEdit={() => openItineraryModal({ itinerary: it })}
                      onStatus={(s) => updateItinerary(it.id, { status: s }, { silent: true })}
                      onDelete={() => removeItinerary(it.id)}
                    />
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      <p className="mt-4 text-right text-xs text-ink-faint">
        Showing{" "}
        <span className="font-bold text-ink">
          <RollingNumber value={filtered.length} delay={0.25} />
        </span>{" "}
        of {itineraries.length} itineraries
      </p>
    </div>
  );
}

function RecordRow({
  it,
  itWritable,
  statusEditable,
  onEdit,
  onStatus,
  onDelete,
}: {
  it: Itinerary;
  itWritable: boolean;
  statusEditable: boolean;
  onEdit: () => void;
  onStatus: (s: Status) => void;
  onDelete: () => void;
}) {
  const meta = STATUS_META[it.status];
  return (
    <div className={cn(
      "group grid grid-cols-[190px_minmax(0,1fr)_140px_76px] items-center gap-3 border-b border-line/60 px-4 py-3 transition-colors last:border-b-0 max-md:min-w-[720px]",
      itWritable ? "hover:bg-black/[0.025]" : "opacity-95",
    )}>
      {/* employee */}
      <div className="flex min-w-0 items-center gap-2.5">
        <EmployeeDot name={it.employeeName ?? "?"} color={it.employeeColor ?? "#999"} size={30} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] leading-tight font-bold">
              {it.employeeName ?? "Unassigned"}
            </span>
            {!itWritable && (
              <span
                title="Read-only — bound to another account."
                className="shrink-0 text-[9px] text-ink-faint"
              >
                🔒
              </span>
            )}
          </div>
          <div className="truncate text-[10px] font-semibold tracking-wide text-ink-faint uppercase">
            {format(parseIso(it.date), "MMM d")} · {ordinal(it.seq)} stop
          </div>
          {it.appointmentTime && (
            <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold text-ink-soft tabular-nums">
              <Clock className="size-2.5 text-accent" />
              {formatTime(it.appointmentTime)}
            </div>
          )}
        </div>
      </div>

      {/* itinerary */}
      <div className="min-w-0">
        <div
          className={cn(
            "truncate text-sm font-semibold",
            it.status === "cancelled" && "text-ink-faint line-through",
          )}
        >
          {it.title}
        </div>
        {(it.locationName || it.notes) && (
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-ink-faint">
            {it.locationName && (
              <span className="flex min-w-0 items-center gap-1">
                <MapPin className="size-3 shrink-0 text-accent/80" />
                <span className="truncate">{it.locationName}</span>
              </span>
            )}
            {it.locationName && it.notes && <span className="shrink-0">·</span>}
            {it.notes && <span className="truncate">{it.notes}</span>}
          </div>
        )}
      </div>

      {/* status switcher */}
      <div className="relative flex items-center justify-end gap-1">
        {itWritable && !statusEditable && (
          <span
            title="Only super admins can change itinerary status."
            className="shrink-0 text-[9px] text-ink-faint"
          >
            🔒
          </span>
        )}
        <div className="relative flex-1">
          <select
            value={it.status}
            onChange={(e) => onStatus(e.target.value as Status)}
            disabled={!statusEditable}
            title={
              statusEditable
                ? undefined
                : itWritable
                  ? "Only super admins can change itinerary status."
                  : "Read-only — bound to another account."
            }
            className={cn(
              "w-full appearance-none rounded-full border py-1.5 pr-6 pl-3 text-[11px] font-bold transition-shadow focus:shadow-sm focus:outline-none",
              statusEditable ? "cursor-pointer" : "cursor-not-allowed opacity-80",
            )}
            style={{
              color: meta.color,
              borderColor: withAlpha(meta.color, 0.35),
              backgroundColor: withAlpha(meta.color, 0.1),
            }}
          >
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_META[s].label}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2"
            style={{ color: meta.color }}
          />
        </div>
      </div>

      {/* actions */}
      {itWritable && (
        <div className="flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            title="Edit"
            onClick={onEdit}
            className="grid size-7 place-items-center rounded-md text-ink-faint transition-colors hover:bg-ink/10 hover:text-ink"
          >
            <Pencil className="size-3.5" />
          </button>
          <ConfirmDelete onConfirm={onDelete} />
        </div>
      )}
    </div>
  );
}
